import type { RequestResult } from 'interfaces/api/client';
import type { GreetingsApiSurface } from 'services/connectivity/greetings-api';
import type { IApiGreeting } from 'store/entities/greetings/interfaces';

import { type ApiClientError, HTTPStatus } from '../../http/types';
import { normalizeError } from '../helpers';
import type { ConfigurationApiClientError } from '../types';

import { greetingsClient } from './client';
import type { CreateGreetingRequest, Greeting, RichGreetingRequest, UpdateGreetingRequest } from './types';

/**
 * Builds a `RequestResult` for the failure path. `RequestResult<T>` types
 * `result` as `T` (no nullability), but the codebase-wide convention for the
 * connectivity layer is `{ result: null, error }` on failure. This helper is
 * the single, documented place where that gap is bridged via a cast, so the
 * rest of the file can stay under strict type checking.
 */
function toErrorResult<T>(error: ConfigurationApiClientError | ApiClientError<Error>): RequestResult<T> {
  return {
    result: null,
    error: normalizeError(error),
  } as unknown as RequestResult<T>;
}

/**
 * Parses a string greeting id into a positive integer, returning `null` if the
 * input is not a strict positive integer string. `Number('')` is `0` and
 * `Number('abc')` is `NaN`, both of which would otherwise reach the API as a
 * malformed call; failing fast on the client side keeps callers from issuing
 * pointless requests on potentially destructive endpoints (`delete_greeting`).
 */
function parseGreetingId(rawId: string): number | null {
  const id = Number(rawId);

  if (!Number.isInteger(id) || id <= 0) {
    return null;
  }

  return id;
}

function invalidIdError(rawId: string): ApiClientError<Error> {
  return {
    http: null,
    status: HTTPStatus.BadRequest,
    local: new Error(`Invalid greeting id: "${rawId}"`),
  };
}

function toRichGreetingRequest(richMessage: NonNullable<Greeting['rich_message']>): RichGreetingRequest {
  return {
    ...richMessage,
    elements: richMessage.elements.map((el) => ({
      ...(el.image && { image: el.image }),
      ...(el.title !== undefined && { title: el.title }),
      ...(el.subtitle !== undefined && { subtitle: el.subtitle }),
      ...(el.buttons && {
        buttons: el.buttons.map(({ button_id: _bid, ...btn }) => btn),
      }),
    })),
  };
}

function toCreateGreetingRequest(greeting: IApiGreeting): CreateGreetingRequest {
  const { id, rich_message: richMessage, ...rest } = greeting as unknown as Greeting;

  const request: CreateGreetingRequest = {
    ...rest,
    active: Boolean(rest.active),
  };

  if (richMessage) {
    request.rich_message = toRichGreetingRequest(richMessage);
  }

  return request;
}

function toUpdateGreetingRequest(payload: Partial<IApiGreeting> & { id: number }): UpdateGreetingRequest {
  const { id, ...rest } = payload as unknown as Partial<Greeting> & { id: number };
  const request: UpdateGreetingRequest = {};

  if (rest.active !== undefined) {
    request.active = Boolean(rest.active);
  }
  if (rest.name !== undefined) {
    request.name = rest.name;
  }
  if (rest.group !== undefined) {
    request.group = rest.group;
  }
  if (rest.rules !== undefined) {
    request.rules = rest.rules;
  }
  if (rest.properties !== undefined) {
    request.properties = rest.properties;
  }
  if (rest.type !== undefined) {
    request.type = rest.type;
  }
  if (rest.active_from !== undefined) {
    request.active_from = rest.active_from;
  }
  if (rest.active_until !== undefined) {
    request.active_until = rest.active_until;
  }
  if (rest.rich_message) {
    request.rich_message = toRichGreetingRequest(rest.rich_message);
  }

  return request;
}

async function fetchAll(): Promise<RequestResult<IApiGreeting[]>> {
  const allGreetings: IApiGreeting[] = [];
  let pageId: string | undefined = undefined;

  do {
    const response = await greetingsClient.list(pageId);

    if (response.error) {
      return toErrorResult<IApiGreeting[]>(response.error);
    }

    allGreetings.push(...(response.result.greetings as unknown as IApiGreeting[]));
    pageId = response.result.next_page_id;
  } while (pageId);

  return { result: allGreetings, error: null };
}

async function fetch(id: string): Promise<RequestResult<IApiGreeting>> {
  const numericId = parseGreetingId(id);
  if (numericId === null) {
    return toErrorResult<IApiGreeting>(invalidIdError(id));
  }

  const response = await greetingsClient.get(numericId);

  if (response.error) {
    return toErrorResult<IApiGreeting>(response.error);
  }

  return { result: response.result as unknown as IApiGreeting, error: null };
}

async function create(greeting: IApiGreeting): Promise<RequestResult<IApiGreeting>> {
  const data = toCreateGreetingRequest(greeting);
  const response = await greetingsClient.create(data);

  if (response.error) {
    return toErrorResult<IApiGreeting>(response.error);
  }

  return {
    result: { ...greeting, id: response.result.id } as unknown as IApiGreeting,
    error: null,
  };
}

async function update(greeting: Partial<IApiGreeting> & { id: number }): Promise<RequestResult<IApiGreeting>> {
  const { id } = greeting;
  const updateRequest = toUpdateGreetingRequest(greeting);
  const response = await greetingsClient.update(id, updateRequest);

  if (response.error) {
    return toErrorResult<IApiGreeting>(response.error);
  }

  // ConfAPI v3.6 `update_greeting` returns no body. The legacy v2 endpoint, by
  // contrast, returns the full updated entity, and consumers (e.g. the
  // `setGreetingVisibility` saga calling `deserializeGreeting(result)`) rely on
  // that. Refresh the entity here so the adapter preserves the legacy contract.
  const refreshed = await greetingsClient.get(id);

  if (refreshed.error) {
    return toErrorResult<IApiGreeting>(refreshed.error);
  }

  return {
    result: refreshed.result as unknown as IApiGreeting,
    error: null,
  };
}

async function remove(greetingId: string): Promise<RequestResult<void>> {
  const numericId = parseGreetingId(greetingId);
  if (numericId === null) {
    return toErrorResult<void>(invalidIdError(greetingId));
  }

  const response = await greetingsClient.delete(numericId);

  if (response.error) {
    return toErrorResult<void>(response.error);
  }

  return { result: undefined, error: null };
}

export const greetingsConfigurationApi: GreetingsApiSurface = {
  fetchAll,
  fetch,
  create,
  update,
  remove,
};
